import React from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  ArrowRight, 
  TrendingUp, 
  TrendingDown, 
  Globe, 
  AlertCircle,
  Brain,
  LineChart
} from 'lucide-react';
import { motion } from 'framer-motion';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import InteractiveCard from './InteractiveCard';

// Sample price trend data
const priceTrendData = [
  { month: 'Jan', coffee: 180, cocoa: 4200 },
  { month: 'Feb', coffee: 195, cocoa: 4400 },
  { month: 'Mar', coffee: 205, cocoa: 4600 },
  { month: 'Apr', coffee: 215, cocoa: 4821 }
];

const marketPrices = [
  {
    crop: "Coffee (Arabica)",
    price: "215.35",
    unit: "USD/lb",
    change: "+2.3%",
    isUp: true,
    forecast: "Upward trend expected",
    confidence: 85
  },
  {
    crop: "Cocoa",
    price: "4,821.00",
    unit: "USD/MT",
    change: "+5.1%",
    isUp: true,
    forecast: "Strong demand continuing",
    confidence: 92
  }
];

const alerts = [
  {
    title: "Drought Alert",
    region: "Horn of Africa",
    impact: "High",
    recommendation: "Consider drought-resistant varieties"
  }
];

const MarketPreview: React.FC = () => {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Market Analysis Card */}
      <InteractiveCard 
        className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
        hoverEffect="glow"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 4 }}
              className="bg-avn-purple-100 p-2 rounded-lg"
            >
              <Brain className="h-5 w-5 text-avn-purple-600" />
            </motion.div>
            <h3 className="text-xl font-semibold">AI Market Analysis</h3>
          </div>
          <Link 
            to="/insights"
            className="flex items-center gap-2 text-avn-purple-600 hover:text-avn-purple-700 transition-colors text-sm group"
          >
            Full Analysis
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Price Trends Chart */}
        <div className="h-64 mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsLineChart data={priceTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line 
                type="monotone" 
                dataKey="coffee" 
                name="Coffee (USD/lb)" 
                stroke="#9333ea" 
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="cocoa" 
                name="Cocoa (USD/MT)" 
                stroke="#7e22ce" 
                strokeWidth={2}
              />
            </RechartsLineChart>
          </ResponsiveContainer>
        </div>

        <div className="text-sm text-gray-600 text-center">
          Global commodity price trends (Last 4 months)
        </div>
      </InteractiveCard>

      {/* Real-time Market Data */}
      <div className="space-y-6">
        {/* Current Prices */}
        <InteractiveCard 
          className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
          hoverEffect="glow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1, repeat: Infinity, repeatDelay: 3 }}
                className="bg-green-100 p-2 rounded-lg"
              >
                <LineChart className="h-5 w-5 text-green-600" />
              </motion.div>
              <h3 className="text-xl font-semibold">Live Prices</h3>
            </div>
          </div>

          <div className="space-y-4">
            {marketPrices.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 p-4 rounded-xl"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{item.crop}</span>
                  <motion.div
                    className={`flex items-center gap-1 text-sm px-2 py-1 rounded-full ${
                      item.isUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 0.3 }}
                  >
                    {item.isUp ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <TrendingDown className="h-4 w-4" />
                    )}
                    {item.change}
                  </motion.div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{item.price} {item.unit}</span>
                  <span className="text-avn-purple-600">{item.confidence}% confidence</span>
                </div>
              </motion.div>
            ))}
          </div>
        </InteractiveCard>

        {/* Market Alerts */}
        <InteractiveCard 
          className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300"
          hoverEffect="glow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity, repeatDelay: 3 }}
                className="bg-amber-100 p-2 rounded-lg"
              >
                <AlertCircle className="h-5 w-5 text-amber-600" />
              </motion.div>
              <h3 className="text-xl font-semibold">Alerts</h3>
            </div>
          </div>

          <div className="space-y-4">
            {alerts.map((alert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-amber-50 p-4 rounded-xl border border-amber-100"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-amber-900">{alert.title}</h4>
                    <div className="text-sm text-amber-700 flex items-center gap-2 mt-1">
                      <Globe className="h-4 w-4" />
                      {alert.region}
                    </div>
                  </div>
                  <span className="text-sm px-2 py-1 rounded-full bg-red-100 text-red-700">
                    {alert.impact}
                  </span>
                </div>
                <div className="text-sm text-amber-700 mt-2">
                  {alert.recommendation}
                </div>
              </motion.div>
            ))}
          </div>

          <Link
            to="/signup"
            className="mt-4 text-sm text-avn-purple-600 hover:text-avn-purple-700 transition-colors flex items-center gap-2 group"
          >
            Get real-time alerts
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </InteractiveCard>
      </div>
    </div>
  );
};

export default MarketPreview;